import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { PROPOSAL_STATUSES } from "@/lib/constants";
import { PageHeader, EmptyState, Field, ProposalStatusBadge, ProposalProgress } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Workflow, Eye } from "lucide-react";

const EMPTY = {
  property_id: "", contact_id: "", status: "Proposta da preparare", proposed_price: 0,
  proposal_date: "", expiry_date: "", response_date: "", next_action: "", notes: "",
};

export default function Proposals() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [converting, setConverting] = useState(false);
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delItem, setDelItem] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/proposals").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/contacts").then((r) => setContacts(r.data));
  }, [load]);

  const deriveOwner = useCallback(async (pid) => {
    try {
      const r = await api.get(`/properties/${pid}`);
      const owner = (r.data.contacts || []).find((c) => c.role === "Proprietario");
      return owner ? owner.id : "";
    } catch { return ""; }
  }, []);

  const openNew = useCallback((presetPropertyId) => {
    setEditing(null);
    setForm({ ...EMPTY, property_id: presetPropertyId || "" });
    setOpen(true);
    if (presetPropertyId) deriveOwner(presetPropertyId).then((ownerId) => {
      if (ownerId) setForm((f) => (f.property_id === presetPropertyId ? { ...f, contact_id: ownerId } : f));
    });
  }, [deriveOwner]);

  useEffect(() => {
    const pid = params.get("property");
    if (pid && properties.length) {
      openNew(pid);
      params.delete("property");
      setParams(params, { replace: true });
    }
  }, [params, properties, openNew, setParams]);

  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const onPickProperty = async (pid) => {
    setForm((f) => ({ ...f, property_id: pid }));
    const ownerId = await deriveOwner(pid);
    if (ownerId) setForm((f) => (f.property_id === pid ? { ...f, contact_id: ownerId } : f));
  };

  const save = async () => {
    if (!form.property_id) { toast.error("Seleziona un immobile"); return; }
    setSaving(true);
    try {
      const prop = properties.find((p) => p.id === form.property_id);
      const contact = contacts.find((c) => c.id === form.contact_id);
      const payload = {
        ...form,
        proposed_price: Number(form.proposed_price) || 0,
        property_label: prop ? prop.address : "",
        contact_name: contact ? contact.name : "",
      };
      if (editing) await api.put(`/proposals/${editing.id}`, payload);
      else await api.post("/proposals", payload);
      toast.success(editing ? "Proposta aggiornata" : "Proposta creata");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/proposals/${delItem.id}`); toast.success("Proposta eliminata"); setDelItem(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const convert = async (p) => {
    setConverting(true);
    try {
      const r = await api.post(`/proposals/${p.id}/convert`);
      toast.success("Proposta trasformata in operazione");
      setOpen(false); load();
      navigate(`/operazioni/${r.data.id}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setConverting(false); }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (search && !`${r.property_label} ${r.contact_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, search, statusFilter]);

  return (
    <div data-testid="proposals-page">
      <PageHeader title="Proposte immobiliari" subtitle={`${filtered.length} proposte`}>
        <Button onClick={() => openNew()} data-testid="new-proposal-btn"><Plus className="h-4 w-4 mr-2" />Nuova Proposta</Button>
      </PageHeader>

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca per immobile o proprietario..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="proposals-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-64" data-testid="proposal-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {[...PROPOSAL_STATUSES, "Convertita in operazione"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Immobile</TableHead><TableHead>Proprietario</TableHead>
              <TableHead className="text-right">Prezzo proposto</TableHead>
              <TableHead className="min-w-[200px]">Avanzamento</TableHead>
              <TableHead>Data proposta</TableHead><TableHead>Scadenza</TableHead>
              <TableHead>Prossima azione</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`proposal-row-${p.id}`}>
                  <TableCell className="font-medium text-slate-800">{p.property_label || "—"}</TableCell>
                  <TableCell className="text-sm">{p.contact_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(p.proposed_price)}</TableCell>
                  <TableCell><ProposalStatusBadge status={p.status} /><div className="mt-1.5"><ProposalProgress status={p.status} showLabel={false} /></div></TableCell>
                  <TableCell className="text-sm">{dateIt(p.proposal_date)}</TableCell>
                  <TableCell className="text-sm">{dateIt(p.expiry_date)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{p.next_action || "—"}</TableCell>
                  <TableCell className="text-right">
                    {p.operation_id ? (
                      <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => navigate(`/operazioni/${p.operation_id}`)} data-testid={`view-operation-row-${p.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Visualizza operazione</Button>
                    ) : (["Proposta accettata", "Acquistato"].includes(p.status) && (
                      <Button size="sm" className="h-8 mr-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => convert(p)} disabled={converting} data-testid={`convert-proposal-row-${p.id}`}><Workflow className="h-3.5 w-3.5 mr-1" />Trasforma in operazione</Button>
                    ))}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-proposal-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => setDelItem(p)} data-testid={`delete-proposal-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && filtered.length === 0 && <EmptyState>Nessuna proposta trovata.</EmptyState>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica Proposta" : "Nuova Proposta"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <Field label="Immobile *">
              <Select value={form.property_id || undefined} onValueChange={onPickProperty}>
                <SelectTrigger data-testid="proposal-property"><SelectValue placeholder="Seleziona immobile" /></SelectTrigger>
                <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Proprietario (dai Contatti)">
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="proposal-contact"><SelectValue placeholder="Seleziona contatto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Stato avanzamento proposta">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="proposal-status"><SelectValue /></SelectTrigger>
                <SelectContent>{PROPOSAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Prezzo proposto (€)"><Input type="number" value={form.proposed_price} onChange={(e) => setForm({ ...form, proposed_price: e.target.value })} data-testid="proposal-price" /></Field>
            <Field label="Data proposta"><Input type="date" value={form.proposal_date || ""} onChange={(e) => setForm({ ...form, proposal_date: e.target.value })} /></Field>
            <Field label="Data scadenza"><Input type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
            <Field label="Data risposta"><Input type="date" value={form.response_date || ""} onChange={(e) => setForm({ ...form, response_date: e.target.value })} /></Field>
            <Field label="Prossima azione"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></Field>
            <Field label="Note della pratica" className="sm:col-span-2"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <div className="sm:col-span-2"><ProposalProgress status={form.status} /></div>
          </div>
          <DialogFooter>
            {editing && ["Proposta accettata", "Acquistato"].includes(editing.status) && !editing.operation_id && (
              <Button onClick={() => convert(editing)} disabled={converting} className="mr-auto bg-emerald-600 hover:bg-emerald-700" data-testid="convert-proposal-btn">
                {converting ? "Conversione..." : "Trasforma in operazione"}
              </Button>
            )}
            {editing && editing.operation_id && (
              <Button variant="outline" onClick={() => navigate(`/operazioni/${editing.operation_id}`)} className="mr-auto" data-testid="view-operation-btn">
                Vai all'operazione collegata
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-proposal-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la proposta?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione eliminerà la proposta per "{delItem?.property_label}" in modo permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-proposal">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
